// Tipado por CAMPO de los diffs de la bitácora + los helpers que comparten DiffList y los
// renderers de diff/presupuesto. Extiende `labels.js` SIN tocarlo: allá viven los mapas
// canónicos, acá sólo lo que falta para leer un diff (qué campo es monetario, de dónde sale
// su moneda, qué valores son canales, qué textos hay que truncar).
// Todo es defensivo: cualquier payload entra, nada lanza.

import {isAmount, isFieldDiff, isPlainObject} from './shapes';
import {HIDDEN_FIELDS, MONEY_FIELDS} from './labels';
import {CHANNEL_FIELDS, formatMoney, humanizeChannel, humanizeFieldLabel, humanizeValue, isNumeric} from './humanize';

// Canales: la humanización vive en humanize.js (la usa también humanizeValue para que los
// enums *_CHANNEL no se escapen crudos por NINGÚN renderer). Acá solo se re-exporta.
export {CHANNEL_FIELDS, humanizeChannel} from './humanize';

// MONEY_FIELDS no cubre los montos de la Línea de Control (más allá de total_budget) ni el
// límite diario de una cuenta: el backend los manda como número crudo dentro de un diff.
export const MONEY_DIFF_FIELDS = new Set([
    ...MONEY_FIELDS,
    'total_budget',
    'allocated_budget',
    'leftover_budget',
    'daily_limit_value',
]);

// Campos cuyo VALOR es una moneda (ISO). De acá sale la moneda de los montos del mismo
// diff — el detail nunca la repite dentro de cada monto.
export const CURRENCY_FIELDS = ['budget_currency', 'daily_limit_currency', 'currency'];

// Orden canónico de un diff de Línea de Control: primero qué línea es, después la plata,
// al final la configuración. Los campos que no están en la lista van después, en el orden
// en que llegaron (nunca se descartan).
export const BU_DIFF_FIELD_ORDER = [
    'name',
    'total_budget',
    'allocated_budget',
    'leftover_budget',
    'budget_currency',
    'rollover',
    'period_type',
    'budget_type',
    'business_unit_type',
    'campaign_channel',
];

// Diffs anidados (AD_ACCOUNT_EDITED / USER_EDITED): el valor del límite antes que su moneda.
export const NESTED_DIFF_FIELD_ORDER = [
    'name',
    'email',
    'daily_limit_type',
    'daily_limit_value',
    'daily_limit_currency',
];

// Notas y URLs de export pueden ser larguísimas: se truncan para que no rompan la fila.
// El dato completo vive en la API — acá no va tooltip (el patrón canónico de Dashbo pide
// un contenedor propio y no se justifica en una celda de diff).
export const LONG_TEXT_MAX_CHARS = 80;

const MAX_EXTRA_PAIRS = 6;

export function truncateText(value, max = LONG_TEXT_MAX_CHARS) {
    if (typeof value !== 'string') return value;
    const text = value.trim();
    if (text.length <= max) return text;
    return `${text.slice(0, max).trimEnd()}…`;
}

export function isMoneyDiffField(field) {
    return MONEY_DIFF_FIELDS.has(field);
}

function currencyOf(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

// La moneda de los montos de un diff sale del PROPIO payload. Si la moneda también cambió,
// cada lado va con la suya y el delta no aplica: montos en monedas distintas no son
// comparables.
export function resolveDiffCurrency(changes) {
    const none = {currency: null, currencyBefore: null, currencyAfter: null, currencyChanged: false};
    if (!isPlainObject(changes)) return none;

    const field = CURRENCY_FIELDS.find(candidate => {
        const value = changes[candidate];
        return isFieldDiff(value) || currencyOf(value);
    });
    if (!field) return none;

    const value = changes[field];
    if (!isFieldDiff(value)) {
        const flat = currencyOf(value);
        return {currency: flat, currencyBefore: flat, currencyAfter: flat, currencyChanged: false};
    }
    const before = currencyOf(value.before);
    const after = currencyOf(value.after);
    const changed = !!before && !!after && before !== after;
    return {
        currency: after || before,
        currencyBefore: changed ? before : (after || before),
        currencyAfter: after || before,
        currencyChanged: changed,
    };
}

function sideCurrency(value, fallback) {
    return (isAmount(value) && currencyOf(value.currency)) || fallback || null;
}

// Un lado de un diff, ya humanizado. null/'' es "—" (campo nuevo o vaciado): acá el vacío
// es parte de la historia del cambio, no "(vacío)".
export function diffSideValue(field, value, t, currency) {
    if (value === null || value === undefined || value === '') return '—';

    if (CHANNEL_FIELDS.has(field)) {
        const channel = humanizeChannel(value, t);
        if (channel) return channel;
    }

    const amount = isAmount(value) ? value.amount : value;
    if ((isMoneyDiffField(field) || isAmount(value)) && isNumeric(amount)) {
        return formatMoney(amount, sideCurrency(value, currency));
    }

    const humanized = humanizeValue(field, value, t);
    return humanized === null ? '—' : truncateText(humanized);
}

// Núcleo compartido: label + los dos lados ya formateados + si el delta es representable.
// `delta` null => no se pinta chip (no numérico, sin cambio o monedas distintas).
export function describeDiff(field, change, t, currencies = {}) {
    const {currency, currencyBefore, currencyAfter} = currencies;
    const before = change ? change.before : undefined;
    const after = change ? change.after : undefined;
    const ownBefore = sideCurrency(before, currencyBefore || currency);
    const ownAfter = sideCurrency(after, currencyAfter || currency);
    const beforeAmount = isAmount(before) ? before.amount : before;
    const afterAmount = isAmount(after) ? after.amount : after;
    const money = isMoneyDiffField(field) || isAmount(before) || isAmount(after);
    const mixedCurrency = !!ownBefore && !!ownAfter && ownBefore !== ownAfter;
    const comparable = money && !mixedCurrency && isNumeric(beforeAmount) && isNumeric(afterAmount);
    return {
        label: humanizeFieldLabel(field, t),
        before: diffSideValue(field, before, t, ownBefore),
        after: diffSideValue(field, after, t, ownAfter),
        delta: comparable ? {before: beforeAmount, after: afterAmount, currency: ownAfter} : null,
    };
}

// Orden de render: los canónicos presentes primero, el resto después tal como llegó.
// Ningún campo se pierde por no estar en la lista (el filtrado de ocultos/no-diffs lo hace
// quien renderiza).
export function orderDiffFields(changes, canonicalOrder = []) {
    if (!isPlainObject(changes)) return [];
    const keys = Object.keys(changes);
    return [
        ...canonicalOrder.filter(field => keys.includes(field)),
        ...keys.filter(field => !canonicalOrder.includes(field)),
    ];
}

export function countVisibleChanges(changes) {
    if (!isPlainObject(changes)) return 0;
    return Object.keys(changes).filter(field => !HIDDEN_FIELDS.has(field) && isFieldDiff(changes[field])).length;
}

// Sufijo "· N cambios" sobre la description del backend. Sin description no hay título
// compuesto: el registry cae a la description limpia (devolver null es la señal).
export function buildChangeCountTitle(entry, t, changes) {
    const description = String((entry && entry.description) || '').trim();
    if (!description) return null;
    const count = countVisibleChanges(changes);
    if (count < 1) return description;
    const key = count === 1
        ? 'activity_log.detail.change_suffix_one'
        : 'activity_log.detail.change_suffix_other';
    return `${description} ${t(key, {count})}`;
}

// Campos del payload que el renderer no consumió: se muestran como pares "label: valor" en
// el pie, nunca se descartan en silencio (los técnicos de HIDDEN_FIELDS sí).
export function extraFieldsSummary(detail, t, consumed = []) {
    if (!isPlainObject(detail)) return null;
    const skip = new Set(consumed);
    const pairs = Object.entries(detail)
        .filter(([field, value]) => !skip.has(field) && !HIDDEN_FIELDS.has(field)
            && value !== null && value !== undefined && value !== '')
        .map(([field, value]) => {
            const rendered = isFieldDiff(value)
                ? `${diffSideValue(field, value.before, t)} → ${diffSideValue(field, value.after, t)}`
                : humanizeValue(field, value, t);
            if (rendered === null || rendered === undefined || rendered === '') return null;
            return `${humanizeFieldLabel(field, t)}: ${truncateText(String(rendered))}`;
        })
        .filter(pair => pair !== null)
        .slice(0, MAX_EXTRA_PAIRS);
    return pairs.length ? pairs.join(' · ') : null;
}
